/**
 * RendererRegistry - Dynamic renderer resolution from subgraph packages
 *
 * Loads renderers on-demand based on metadata configuration.
 * Supports lazy-loading to reduce bundle size.
 *
 * Pattern: metadata.customRenderers = {
 *   "Employee": "PayrollEmployeeRenderer"
 * } → RendererRegistry.get("PayrollEmployeeRenderer")
 */

import React from 'react';

// Dynamic imports for renderers

// Payroll Renderers
const PayrollRenderers = {
  PayrollEmployeeRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollEmployeeRenderer }))
  ),
  PayrollCompensationRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollCompensationRenderer }))
  ),
  PayrollBenefitEnrollmentRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollBenefitEnrollmentRenderer }))
  ),
  PayrollRunRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollRunRenderer }))
  ),
  PayrollSlaRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollSlaRenderer }))
  ),
  PayrollAnalyticsRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollAnalyticsRenderer }))
  ),
  PayrollExceptionRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollExceptionRenderer }))
  ),
  PayrollAuditRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollAuditRenderer }))
  ),
  PayrollReportRenderer: React.lazy(
    () => import('@ferp/subgraph-payroll/renderers').then(m => ({ default: m.PayrollReportRenderer }))
  ),
};

// FHIR Renderers
const FhirRenderers = {
  FhirPatientRenderer: React.lazy(
    () => import('@ferp/subgraph-fhir/renderers').then(m => ({ default: m.FhirPatientRenderer }))
  ),
  FhirObservationRenderer: React.lazy(
    () => import('@ferp/subgraph-fhir/renderers').then(m => ({ default: m.FhirObservationRenderer }))
  ),
  // DRR-232: Domain-specific renderers for generic plan pages
  FhirCarePlanQueueRenderer: React.lazy(
    () => import('@ferp/subgraph-fhir/renderers').then(m => ({ default: m.FhirCarePlanQueueRenderer }))
  ),
  FhirShiftWorkRenderer: React.lazy(
    () => import('@ferp/subgraph-fhir/renderers').then(m => ({ default: m.FhirShiftWorkRenderer }))
  ),
  FhirControlledSubstanceRenderer: React.lazy(
    () => import('@ferp/subgraph-fhir/renderers').then(m => ({ default: m.FhirControlledSubstanceRenderer }))
  ),
};

// ERP Renderers
const ErpRenderers = {
  ErpGlAccountRenderer: React.lazy(
    () => import('@ferp/subgraph-erp/renderers').then(m => ({ default: m.ErpGlAccountRenderer }))
  ),
  ErpVendorRenderer: React.lazy(
    () => import('@ferp/subgraph-erp/renderers').then(m => ({ default: m.ErpVendorRenderer }))
  ),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RendererComponent = React.ComponentType<{ entity: Record<string, any> }>;

const RENDERER_MAP: Record<string, RendererComponent> = {
  ...PayrollRenderers,
  ...FhirRenderers,
  ...ErpRenderers,
} as Record<string, RendererComponent>;

/**
 * RendererRegistry - Global renderer resolver
 */
export class RendererRegistry {
  static get(rendererName: string): React.ComponentType<{ entity: Record<string, any> }> | undefined {
    return RENDERER_MAP[rendererName];
  }

  static getAll(): Record<string, React.ComponentType<{ entity: Record<string, any> }>> {
    return RENDERER_MAP;
  }

  static register(
    name: string,
    component: React.ComponentType<{ entity: Record<string, any> }>
  ): void {
    RENDERER_MAP[name] = component;
  }
}

/**
 * Hook to get renderer by name
 */
export function useRenderer(rendererName: string | undefined): React.ComponentType<{ entity: Record<string, any> }> | null {
  return React.useMemo(() => {
    if (!rendererName) return null;
    return RendererRegistry.get(rendererName) || null;
  }, [rendererName]);
}

/**
 * Hook to extract renderer name from metadata
 */
export function useRendererNameFromMetadata(
  metadata: any,
  entityType: string
): string | undefined {
  return React.useMemo(() => {
    if (!metadata?.metadata) return undefined;
    try {
      const meta = typeof metadata.metadata === 'string'
        ? JSON.parse(metadata.metadata)
        : metadata.metadata;
      return meta.customRenderers?.[entityType];
    } catch {
      return undefined;
    }
  }, [metadata, entityType]);
}
